import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Button from "@mui/material/Button";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SearchPanel from "@widgets/SearchPanel";

interface Emp {
  cd_emp: string;
  nm_emp: string;
  [key: string]: any;
}

interface Props {
  selected?: Emp[];
}

const POPUP_CODE = "SN_SFA_EXPENSE_REG_001_POP_02";

// 경비등록 직원명 선택: 왼쪽 사원 목록 → 오른쪽 선택된 사원. 처음부터 두 목록을 함께 보여 준다.
// 조회조건 = sys_popupctrl, 칼럼 = sys_popupgridcolumn(grid1 사원 / grid2 선택된 사원),
// 조회 쿼리 = sys_query sn_sfa_expense_reg_001_pop_02_01 (모두 팝업 코드로 등록, 공통 사원도움창 POPUP_EMP 에서 복사)
const SN_SFA_EXPENSE_REG_001_POP_02 = forwardRef(
  ({ selected = [] }: Props, ref) => {
    const searchRef = useRef<any>(null);
    const empGridRef = useRef<any>(null);
    const selectedGridRef = useRef<any>(null);

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [empColumns, setEmpColumns] = useState<any[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<any[]>([]);
    const [emps, setEmps] = useState<Emp[]>([]);
    const [picked, setPicked] = useState<Emp[]>(selected);

    useImperativeHandle(ref, () => ({
      onSearch,
      // 공통 팝업 확인 → { rows } 그대로 반환(0명이어도 닫힌다)
      getSelectedValue: () => ({ rows: picked }),
    }));

    useEffect(() => {
      const init = async () => {
        const rtn: any = await getInitPopUpData(POPUP_CODE, {});
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setEmpColumns(rtn.grid?.grid1 ?? []);
        setSelectedColumns(rtn.grid?.grid2 ?? []);
        onSearch(rtn.formData);
      };
      init();
    }, []);

    // 오른쪽 목록은 행 데이터 자체가 바뀌어(추가·빼기) 헤더 전체선택 체크가 다시 계산되지 않는다
    // (공통 헤더 체크박스는 셀 값·필터 변경 때만 갱신). 목록이 바뀐 뒤 체크를 풀고 헤더를 다시 그린다.
    // 처음 열 때는 풀 체크가 없고 그리드도 아직 준비 전이라 건너뛴다.
    const pickedChangedRef = useRef(false);
    useEffect(() => {
      if (!pickedChangedRef.current) {
        pickedChangedRef.current = true;
        return;
      }
      const frame = requestAnimationFrame(() => {
        try {
          selectedGridRef.current?.clearCheckBox?.();
        } catch {
          // 그리드가 아직 준비되지 않았으면 다음 변경 때 다시 맞춘다
        }
      });
      return () => cancelAnimationFrame(frame);
    }, [picked]);

    const onSearch = async (params?: any) => {
      const data: any = await fnGetDataSet({
        id: `${POPUP_CODE.toLowerCase()}_01`,
        params: params ?? searchRef.current?.getFormdata(),
      });
      setEmps(data?.[0] ?? []);
    };

    const add = (rows: Emp[]) => {
      if (rows.length === 0) return;
      setPicked((prev) => [
        ...prev,
        ...rows
          .filter((row) => !prev.some((p) => p.cd_emp === row.cd_emp))
          .map(({ KeyField, state, fixCheckbox, ...row }: any) => row),
      ]);
    };
    const remove = (rows: Emp[]) => {
      if (rows.length === 0) return;
      setPicked((prev) =>
        prev.filter((p) => !rows.some((row) => row.cd_emp === p.cd_emp))
      );
    };

    // 체크한 행이 없으면 포커스된 행 하나를 옮긴다.
    const targetRows = (gridRef: React.MutableRefObject<any>): Emp[] => {
      const checked = gridRef.current?.getCheckedFilteredRows?.() ?? [];
      if (checked.length > 0) return checked;
      const focused = gridRef.current?.focusedRowNode?.()?.data;
      return focused ? [focused] : [];
    };

    const moveButton = (label: string, title: string, onClick: () => void) => (
      <Button
        type="button"
        variant="outlined"
        title={title}
        aria-label={title}
        onClick={onClick}
        sx={{ minWidth: 0, width: 40, height: 32, fontSize: 18 }}
      >
        {label}
      </Button>
    );

    return (
      <div className="h-full flex flex-col" style={{ minHeight: 0 }}>
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          popupRef={ref}
        />
        <div style={{ textAlign: "center", margin: "8px 0" }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => onSearch()}
            sx={{ width: 100 }}
          >
            조회
          </Button>
        </div>
        <div className="flex-1 flex gap-2" style={{ minHeight: 0 }}>
          <div className="flex flex-col" style={{ flex: 3, minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>사원</div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <SnGrid
                ref={empGridRef}
                columnConfig={empColumns}
                rowData={emps}
                etcProps={{
                  rowSelection: "multiple",
                  isEditable: false,
                  ischeckboxSelection: true,
                  suppressInitialCellFocus: true,
                }}
                events={{ onRowDoubleClicked: (e: any) => add([e.data]) }}
              />
            </div>
          </div>
          <div className="flex flex-col justify-center gap-2">
            {moveButton("→", "선택한 사원 추가", () => {
              add(targetRows(empGridRef));
              // 옮긴 뒤 왼쪽 체크(헤더 전체선택 포함)를 풀어 다음 선택과 섞이지 않게 한다
              empGridRef.current?.clearCheckBox?.();
            })}
            {moveButton("←", "선택한 사원 빼기", () =>
              remove(targetRows(selectedGridRef))
            )}
          </div>
          <div className="flex flex-col" style={{ flex: 2, minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              선택된 사원 ({picked.length}명)
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <SnGrid
                ref={selectedGridRef}
                columnConfig={selectedColumns}
                rowData={picked}
                etcProps={{
                  rowSelection: "multiple",
                  isEditable: false,
                  ischeckboxSelection: true,
                  suppressInitialCellFocus: true,
                }}
                events={{ onRowDoubleClicked: (e: any) => remove([e.data]) }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default SN_SFA_EXPENSE_REG_001_POP_02;
